/**
 * The vocabulary of a Data Export row filter.
 *
 * Lives here, rather than in the API or the web app, because both have to
 * agree on it exactly: the page builds and saves filters, the API validates
 * and applies them, and a saved export runs months later on a schedule with
 * nobody watching. An operator known to one side and not the other is either
 * a refused save or a filter that silently stops narrowing the file.
 */

/**
 * The comparisons a row filter can make.
 *
 * Deliberately a short list of things a person would say out loud — "species
 * is beef", "hanging weight at least 600", "kill date is blank" — rather than
 * a query language.
 */
export const EXPORT_FILTER_OPERATORS = [
  'eq',
  'neq',
  'contains',
  'notContains',
  'gt',
  'gte',
  'lt',
  'lte',
  'blank',
  'notBlank',
] as const;
export type ExportFilterOperator = (typeof EXPORT_FILTER_OPERATORS)[number];

/** Operators that test the cell alone, with nothing to compare it to. */
export const VALUELESS_EXPORT_FILTER_OPERATORS: readonly ExportFilterOperator[] =
  ['blank', 'notBlank'];

/**
 * The operators a BLANK cell passes.
 *
 * A blank cell is blank, is not equal to anything and does not contain
 * anything — so it passes these and fails every other test. A column the rows
 * do not carry at all reads as blank in every row, which makes this the list
 * of filters that let EVERY row through when their column is missing; the
 * page uses it to say so.
 */
export const BLANK_PASSING_EXPORT_FILTER_OPERATORS: readonly ExportFilterOperator[] =
  ['blank', 'neq', 'notContains'];

/**
 * How several filters combine: a row must pass `all` of them, or `any` one.
 *
 * One switch for the whole list rather than nested groups — the page is for
 * narrowing a file, not for writing queries.
 */
export const EXPORT_FILTER_MODES = ['all', 'any'] as const;
export type ExportFilterMode = (typeof EXPORT_FILTER_MODES)[number];

/** Narrow a stored or URL-borne value to an operator this build knows. */
export function isExportFilterOperator(
  value: unknown,
): value is ExportFilterOperator {
  return (EXPORT_FILTER_OPERATORS as readonly unknown[]).includes(value);
}
